import { useState, useEffect, useRef, useCallback } from 'react';
import { Loader2, Eye, Play, RefreshCw, AlertTriangle, FolderTree, Search, Copy, Info, MapPin } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { diagnosticsAPI } from '../../lib/api';
import { toast } from 'sonner';
import { getErrorMessage } from '../../lib/errors';

function fmtInt(n) {
  if (n == null) return '-';
  return Number(n).toLocaleString('en-GB');
}

const POLL_INTERVAL_MS = 2000;

// Preview and Apply are independent server-side operations (separate
// is_operation_running guards per operation) -- they can legitimately both
// be "running" at once (e.g. triggered from two tabs). Each gets its own
// poll/elapsed refs and state below so recovering/polling one can never
// stop the other's poll; sharing a single ref (the original design here)
// meant only one operation's live progress could ever be visible at a time.
function useJobPolling(operation, { onCompleted, onFailed, onProgress }) {
  const [polling, setPolling] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const pollRef = useRef(null);
  const elapsedRef = useRef(null);
  const startTimeRef = useRef(null);

  const stop = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
    if (elapsedRef.current) {
      clearInterval(elapsedRef.current);
      elapsedRef.current = null;
    }
    startTimeRef.current = null;
    setPolling(false);
  }, []);

  useEffect(() => stop, [stop]);

  const start = useCallback((id) => {
    stop();
    setPolling(true);
    setElapsed(0);
    startTimeRef.current = Date.now();

    elapsedRef.current = setInterval(() => {
      setElapsed(Math.floor((Date.now() - startTimeRef.current) / 1000));
    }, 1000);

    pollRef.current = setInterval(async () => {
      try {
        const { data } = await diagnosticsAPI.drivePathAuditJobStatus(id);
        if (data.status === 'completed') {
          stop();
          onCompleted(data.result);
        } else if (data.status === 'failed') {
          stop();
          onFailed(data.error, data.progress || null);
        } else if (data.status === 'running') {
          onProgress(data.progress || null);
        }
      } catch (err) {
        stop();
        toast.error(getErrorMessage(err));
      }
    }, POLL_INTERVAL_MS);
  }, [stop, onCompleted, onFailed, onProgress]);

  return { polling, elapsed, start };
}

export function DrivePathAuditPanel() {
  const [dispatching, setDispatching] = useState(false);
  const [preview, setPreview] = useState(null);
  const [previewProgress, setPreviewProgress] = useState(null);
  const [previewFailed, setPreviewFailed] = useState(null);
  const [applyResult, setApplyResult] = useState(null);
  const [applyProgress, setApplyProgress] = useState(null);
  const [applyFailed, setApplyFailed] = useState(null);
  const [driveSettings, setDriveSettings] = useState(null);
  // Self-chunking Apply chain: multi-run summary (status/runs_count) polled
  // separately from the per-run job (applyPoll above) since a chain keeps
  // progressing across the gap between one run finishing and its successor
  // starting, when no job is actively "running" to poll.
  const [chain, setChain] = useState(null);
  const [chainStopping, setChainStopping] = useState(false);

  // Read-only diagnostics on top of the audit: per-document chain
  // explainer, duplicate-sibling folder census, and the static race
  // write-up. None of these write to Drive or the database.
  const [explainResult, setExplainResult] = useState(null);
  const [explainProgress, setExplainProgress] = useState(null);
  const [explainFailed, setExplainFailed] = useState(null);
  const [censusResult, setCensusResult] = useState(null);
  const [censusProgress, setCensusProgress] = useState(null);
  const [censusFailed, setCensusFailed] = useState(null);
  // Delete-empty-twin: the one action acting on the census output.
  // Tracks which specific folder_id is currently being previewed/applied
  // so only that row's button shows a spinner.
  const [twinBusyId, setTwinBusyId] = useState(null);
  const [raceAudit, setRaceAudit] = useState(null);
  const [raceAuditLoading, setRaceAuditLoading] = useState(false);
  const [locateTreeResult, setLocateTreeResult] = useState(null);
  const [locateTreeProgress, setLocateTreeProgress] = useState(null);
  const [locateTreeFailed, setLocateTreeFailed] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await diagnosticsAPI.driveSettingsGet();
        setDriveSettings(data);
      } catch {
        // Non-fatal: the destination banner just won't render; Apply will
        // still surface the real error if the root turns out to be unset.
      }
    })();
  }, []);

  const fetchChainStatus = useCallback(async () => {
    try {
      const { data } = await diagnosticsAPI.drivePathAuditChainStatus();
      setChain(data?.chain || null);
    } catch {
      // Non-fatal: the chain summary just won't render/update this tick.
    }
  }, []);

  const previewPoll = useJobPolling('preview', {
    onCompleted: useCallback((result) => {
      setPreviewProgress(null);
      setPreviewFailed(null);
      setPreview(result);
      toast.success('Preview complete');
    }, []),
    // Progress from the failing poll tick is kept (not nulled) -- an
    // operator watching a run die mid-batch should still see how far it
    // got, which is the entire point of tracking progress in the first
    // place. It's cleared again the next time a fresh Preview is started.
    onFailed: useCallback((error, lastProgress) => {
      setPreviewProgress((prev) => lastProgress ?? prev);
      setPreviewFailed(error);
      toast.error(error ?? 'preview failed');
    }, []),
    onProgress: useCallback((progress) => setPreviewProgress(progress), []),
  });

  const applyPoll = useJobPolling('apply', {
    onCompleted: useCallback((result) => {
      setApplyProgress(null);
      setApplyFailed(null);
      setApplyResult(result);
      setPreview(null);
      // truncated=true means this was one run within a still-converging
      // chain (see routes.admin_drive_path_audit._finish_chain_run) -- the
      // successor is scheduled automatically, no action needed here beyond
      // refreshing the chain summary so the run count/status update promptly.
      if (result?.truncated) {
        toast.success(
          `Run complete: ${result?.moved ?? 0} moved so far, ${result?.failed ?? 0} failed -- continuing chain`
        );
      } else {
        toast.success(
          `Relocation complete: ${result?.moved ?? 0} moved, ${result?.failed ?? 0} failed`
        );
      }
      fetchChainStatus();
    }, []),
    onFailed: useCallback((error, lastProgress) => {
      setApplyProgress((prev) => lastProgress ?? prev);
      setApplyFailed(error);
      toast.error(error ?? 'apply failed');
    }, []),
    onProgress: useCallback((progress) => setApplyProgress(progress), []),
  });

  const explainPoll = useJobPolling('explain', {
    onCompleted: useCallback((result) => {
      setExplainProgress(null);
      setExplainFailed(null);
      setExplainResult(result);
      toast.success(`Explained ${result?.explained ?? 0} document(s)`);
    }, []),
    onFailed: useCallback((error, lastProgress) => {
      setExplainProgress((prev) => lastProgress ?? prev);
      setExplainFailed(error);
      toast.error(error ?? 'explain failed');
    }, []),
    onProgress: useCallback((progress) => setExplainProgress(progress), []),
  });

  const censusPoll = useJobPolling('census', {
    onCompleted: useCallback((result) => {
      setCensusProgress(null);
      setCensusFailed(null);
      setCensusResult(result);
      toast.success(`Census complete: ${result?.duplicate_group_count ?? 0} duplicate group(s)`);
    }, []),
    onFailed: useCallback((error, lastProgress) => {
      setCensusProgress((prev) => lastProgress ?? prev);
      setCensusFailed(error);
      toast.error(error ?? 'census failed');
    }, []),
    onProgress: useCallback((progress) => setCensusProgress(progress), []),
  });

  const locateTreePoll = useJobPolling('locate_tree', {
    onCompleted: useCallback((result) => {
      setLocateTreeProgress(null);
      setLocateTreeFailed(null);
      setLocateTreeResult(result);
      toast.success('Locate audited tree complete');
    }, []),
    onFailed: useCallback((error, lastProgress) => {
      setLocateTreeProgress((prev) => lastProgress ?? prev);
      setLocateTreeFailed(error);
      toast.error(error ?? 'locate audited tree failed');
    }, []),
    onProgress: useCallback((progress) => setLocateTreeProgress(progress), []),
  });

  // On mount: recover the previous preview/apply jobs independently so a
  // page refresh shows the last finished (or failed, with its last-known
  // progress) run for both operations, or resumes polling either/both if
  // still in flight -- instead of going back to a blank one-shot block.
  useEffect(() => {
    (async () => {
      try {
        const { data } = await diagnosticsAPI.drivePathAuditJobLatest('preview');
        const job = data?.job;
        if (!job) {
          // no-op
        } else if (job.status === 'completed' && job.result) {
          setPreview(job.result);
        } else if (job.status === 'running') {
          setPreviewProgress(job.progress || null);
          previewPoll.start(job._id);
        } else if (job.status === 'failed') {
          setPreviewProgress(job.progress || null);
          setPreviewFailed(job.error);
        }
      } catch {
        // Ignore initial status check failures.
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await diagnosticsAPI.drivePathAuditJobLatest('apply');
        const job = data?.job;
        if (!job) return;
        if (job.status === 'completed' && job.result) {
          setApplyResult(job.result);
        } else if (job.status === 'running') {
          setApplyProgress(job.progress || null);
          applyPoll.start(job._id);
        } else if (job.status === 'failed') {
          setApplyProgress(job.progress || null);
          setApplyFailed(job.error);
        }
      } catch {
        // Ignore initial status check failures.
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Initial load, independent of whether a chain happens to be running.
  useEffect(() => {
    fetchChainStatus();
  }, [fetchChainStatus]);

  // While a chain is running, keep polling even during the gap between one
  // run finishing and its successor starting (when applyPoll itself is
  // briefly idle -- no job is "running" to poll in that window).
  useEffect(() => {
    if (chain?.status !== 'running') return undefined;
    const id = setInterval(fetchChainStatus, POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [chain?.status, fetchChainStatus]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await diagnosticsAPI.drivePathAuditJobLatest('explain');
        const job = data?.job;
        if (!job) return;
        if (job.status === 'completed' && job.result) {
          setExplainResult(job.result);
        } else if (job.status === 'running') {
          setExplainProgress(job.progress || null);
          explainPoll.start(job._id);
        } else if (job.status === 'failed') {
          setExplainProgress(job.progress || null);
          setExplainFailed(job.error);
        }
      } catch {
        // Ignore initial status check failures.
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await diagnosticsAPI.drivePathAuditJobLatest('census');
        const job = data?.job;
        if (!job) return;
        if (job.status === 'completed' && job.result) {
          setCensusResult(job.result);
        } else if (job.status === 'running') {
          setCensusProgress(job.progress || null);
          censusPoll.start(job._id);
        } else if (job.status === 'failed') {
          setCensusProgress(job.progress || null);
          setCensusFailed(job.error);
        }
      } catch {
        // Ignore initial status check failures.
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await diagnosticsAPI.drivePathAuditJobLatest('locate_tree');
        const job = data?.job;
        if (!job) return;
        if (job.status === 'completed' && job.result) {
          setLocateTreeResult(job.result);
        } else if (job.status === 'running') {
          setLocateTreeProgress(job.progress || null);
          locateTreePoll.start(job._id);
        } else if (job.status === 'failed') {
          setLocateTreeProgress(job.progress || null);
          setLocateTreeFailed(job.error);
        }
      } catch {
        // Ignore initial status check failures.
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const runPreview = async () => {
    setDispatching(true);
    setPreview(null);
    setPreviewProgress(null);
    setPreviewFailed(null);
    try {
      const { data } = await diagnosticsAPI.drivePathAuditPreviewStart();
      previewPoll.start(data.job_id);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setDispatching(false);
    }
  };

  const runApply = async () => {
    const count = preview ? preview.mismatched_count : null;
    if (!window.confirm(
      count != null
        ? `This will move ${count} misfiled file(s) to their canonical Drive location. Continue?`
        : 'This will move any misfiled files to their canonical Drive location. Continue?'
    )) return;

    setDispatching(true);
    setApplyResult(null);
    setApplyProgress(null);
    setApplyFailed(null);
    try {
      const { data } = await diagnosticsAPI.drivePathAuditApplyStart();
      if (data.job_id) {
        applyPoll.start(data.job_id);
      } else if (data.status === 'chain_running') {
        toast.error('A chain is already running -- it will continue on its own.');
      } else if (data.status === 'chain_unavailable') {
        toast.error('Chain became unavailable while starting -- try again.');
      }
      fetchChainStatus();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setDispatching(false);
    }
  };

  const stopChain = async () => {
    if (!chain?._id) return;
    if (!window.confirm(
      'Stop the running Apply chain? The current run finishes its time window normally; no further run is scheduled after that.'
    )) return;
    setChainStopping(true);
    try {
      await diagnosticsAPI.drivePathAuditChainStop(chain._id);
      toast.success('Stop requested -- the chain will halt after its current run finishes.');
      fetchChainStatus();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setChainStopping(false);
    }
  };

  const runExplain = async () => {
    setDispatching(true);
    setExplainResult(null);
    setExplainProgress(null);
    setExplainFailed(null);
    try {
      const { data } = await diagnosticsAPI.drivePathAuditExplainStart();
      explainPoll.start(data.job_id);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setDispatching(false);
    }
  };

  const runCensus = async () => {
    setDispatching(true);
    setCensusResult(null);
    setCensusProgress(null);
    setCensusFailed(null);
    try {
      const { data } = await diagnosticsAPI.drivePathAuditDuplicateCensusStart();
      censusPoll.start(data.job_id);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setDispatching(false);
    }
  };

  // Delete empty twin: the one action acting on the census output.
  // Preview-then-confirm-then-apply, synchronous (no job/poll) since it's
  // a handful of Drive calls on one already-known folder_id, never
  // re-derived by name match. Re-runs the census on success so the card
  // reflects the new group count without a manual re-click.
  const runDeleteEmptyTwin = async (group, folderId) => {
    setTwinBusyId(folderId);
    try {
      const { data: twinPreview } = await diagnosticsAPI.drivePathAuditDeleteEmptyTwinPreview(
        folderId, group.parent_id, group.name,
      );
      if (!twinPreview.safe_to_delete) {
        toast.error(
          twinPreview.target_is_canonical
            ? `Folder ${folderId} is the folder writers currently resolve to for ${group.parent_path} / ${group.name} -- refusing to delete the canonical sibling.`
            : `Folder ${folderId} is not empty (${twinPreview.file_count} file(s), ${twinPreview.subfolder_count} subfolder(s)) -- refusing to delete.`
        );
        return;
      }
      const parentChainStr = (twinPreview.parent_chain || []).map((c) => c.name).reverse().join(' / ') || '(unresolved)';
      if (!window.confirm(
        `Delete empty twin folder?\n\nID: ${folderId}\nName: ${group.name}\nParent chain: ${parentChainStr}\nFiles: ${twinPreview.file_count}, subfolders: ${twinPreview.subfolder_count}\nSurviving canonical sibling: ${twinPreview.picked_by_current_resolution}\n\nThis moves the folder to Drive Trash and cannot be undone from this panel.`
      )) return;

      const { data: applyResultTwin } = await diagnosticsAPI.drivePathAuditDeleteEmptyTwinApply(
        folderId, group.parent_id, group.name,
      );
      toast.success(`Deleted twin folder ${applyResultTwin.deleted_id}`);
      runCensus();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setTwinBusyId(null);
    }
  };

  const runLocateTree = async () => {
    setDispatching(true);
    setLocateTreeResult(null);
    setLocateTreeProgress(null);
    setLocateTreeFailed(null);
    try {
      const { data } = await diagnosticsAPI.drivePathAuditLocateTreeStart();
      locateTreePoll.start(data.job_id);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setDispatching(false);
    }
  };

  const runRaceAudit = async () => {
    setRaceAuditLoading(true);
    try {
      const { data } = await diagnosticsAPI.drivePathAuditRaceAudit();
      setRaceAudit(data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setRaceAuditLoading(false);
    }
  };

  const previewPolling = previewPoll.polling;
  const applyPolling = applyPoll.polling;
  const explainPolling = explainPoll.polling;
  const censusPolling = censusPoll.polling;
  const locateTreePolling = locateTreePoll.polling;
  const busy = dispatching || previewPolling || applyPolling || explainPolling || censusPolling || locateTreePolling;

  // Chain-wide cumulative figures live on the latest run's own job
  // progress/result, not on the chain doc itself (apply_relocation's
  // seed threading already makes them cumulative across every run in the
  // chain -- see services/drive_audit_chain.py's module docstring).
  const chainRunning = chain?.status === 'running';
  const liveApply = applyProgress || applyResult;
  const chainChecked = applyProgress?.scanned ?? applyResult?.total_scanned;
  const chainTotal = liveApply?.total_candidates;
  const chainUnresolvable = liveApply?.skipped_by_reason?.unresolvable;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">Drive Filing-Path Audit</CardTitle>
        <p className="text-xs text-muted-foreground mt-0.5">
          Compares where every filed invoice/receipt currently sits in Drive against the one
          canonical path (jurisdiction / entity / year / month / Invoices-Inbound|Outbound or
          Receipts). Preview live-checks each file's current Drive location without writing;
          Apply re-checks fresh and moves (reparents, never copy-and-orphan) anything mismatched,
          then restamps file_url. Re-running Apply is a no-op once nothing is left mismatched.
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        {driveSettings && (
          driveSettings.configured ? (
            <div className="border rounded-lg p-2 flex items-start gap-2 bg-muted/30">
              <FolderTree className="h-4 w-4 mt-0.5 flex-shrink-0 text-muted-foreground" />
              <div className="min-w-0">
                <p className="text-xs font-medium">Canonical tree root</p>
                <p className="text-xs text-muted-foreground font-mono break-all">
                  {driveSettings.drive_id ? (driveSettings.drive_name || driveSettings.drive_id) : 'My Drive'}
                  {' › '}{driveSettings.root_folder_name || driveSettings.root_folder_id}
                  {' › '}Jurisdiction {' › '}Entity{' › '}Year{' › '}Month{' › '}Invoices-Inbound|Outbound or Receipts
                </p>
              </div>
            </div>
          ) : (
            <div className="border-2 rounded-lg p-2 border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-950/20 flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-amber-600 flex-shrink-0" />
              <p className="text-xs text-amber-700 dark:text-amber-400">
                Drive destination root is not configured. Set it under Integrations &gt; Email
                &amp; Drive -- Apply is disabled until then.
              </p>
            </div>
          )
        )}

        <div className="flex items-center gap-2 flex-wrap">
          <Button onClick={runPreview} disabled={busy} size="sm">
            {previewPolling ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Eye className="h-4 w-4 mr-2" />}
            Preview
          </Button>

          <Button
            onClick={runApply}
            disabled={busy || chainRunning || (driveSettings && !driveSettings.configured)}
            size="sm"
            variant={preview && preview.mismatched_count > 0 ? 'default' : 'outline'}
          >
            {(applyPolling || chainRunning) ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Play className="h-4 w-4 mr-2" />}
            {chainRunning
              ? `Chain running, run ${fmtInt(chain.runs_count)}, ${fmtInt(chainChecked)}/${fmtInt(chainTotal)}`
              : (preview ? `Apply (${fmtInt(preview.mismatched_count)} misfiled)` : 'Apply')}
          </Button>

          {chainRunning && (
            <Button onClick={stopChain} disabled={chainStopping} size="sm" variant="outline">
              {chainStopping ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Stop Chain
            </Button>
          )}

          {previewPolling && (
            <span className="text-xs text-muted-foreground tabular-nums flex items-center gap-1">
              <RefreshCw className="h-3 w-3 animate-spin" />
              preview {previewPoll.elapsed}s
            </span>
          )}
          {applyPolling && (
            <span className="text-xs text-muted-foreground tabular-nums flex items-center gap-1">
              <RefreshCw className="h-3 w-3 animate-spin" />
              apply {applyPoll.elapsed}s
            </span>
          )}
        </div>

        {chain && (
          <div className="border rounded-lg p-2 text-xs space-y-0.5 bg-muted/20">
            <div className="font-medium flex items-center gap-1">
              Apply chain <Badge variant={chainRunning ? 'default' : chain.status === 'error' ? 'destructive' : 'outline'}>
                {chain.status}
              </Badge>
            </div>
            <p className="text-muted-foreground tabular-nums">
              run {fmtInt(chain.runs_count)}
              {chainTotal != null && (
                <>
                  {' -- checked '}{fmtInt(chainChecked)}{' of '}{fmtInt(chainTotal)}
                  {', moved '}{fmtInt(liveApply?.moved)}
                  {', failed '}{fmtInt(liveApply?.failed)}
                  {', unresolvable '}{fmtInt(chainUnresolvable)}
                </>
              )}
            </p>
            {chain.status === 'error' && chain.error && (
              <p className="text-red-600 dark:text-red-400">{chain.error}</p>
            )}
          </div>
        )}

        {/* ── Read-only diagnostics: explain / duplicate census / race audit ── */}
        <div className="border rounded-lg p-2 space-y-2 bg-muted/20">
          <p className="text-xs font-medium">
            Misfile diagnostics <span className="text-muted-foreground font-normal">(read-only, for a Preview/Apply disagreement)</span>
          </p>
          <div className="flex items-center gap-2 flex-wrap">
            <Button onClick={runExplain} disabled={busy} size="sm" variant="outline">
              {explainPolling ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Search className="h-4 w-4 mr-2" />}
              Explain misfiled sample
            </Button>
            <Button onClick={runCensus} disabled={busy} size="sm" variant="outline">
              {censusPolling ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Copy className="h-4 w-4 mr-2" />}
              Duplicate-sibling census
            </Button>
            <Button onClick={runRaceAudit} disabled={raceAuditLoading} size="sm" variant="outline">
              {raceAuditLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Info className="h-4 w-4 mr-2" />}
              Race audit
            </Button>
            <Button onClick={runLocateTree} disabled={busy} size="sm" variant="outline">
              {locateTreePolling ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <MapPin className="h-4 w-4 mr-2" />}
              Locate audited tree
            </Button>
            {locateTreePolling && (
              <span className="text-xs text-muted-foreground tabular-nums flex items-center gap-1">
                <RefreshCw className="h-3 w-3 animate-spin" />
                locating {locateTreePoll.elapsed}s{locateTreeProgress ? ` -- ${locateTreeProgress.stage}${locateTreeProgress.scanned != null ? ` (${fmtInt(locateTreeProgress.scanned)}/${fmtInt(locateTreeProgress.total)})` : ''}` : ''}
              </span>
            )}
            {explainPolling && (
              <span className="text-xs text-muted-foreground tabular-nums flex items-center gap-1">
                <RefreshCw className="h-3 w-3 animate-spin" />
                explain {explainPoll.elapsed}s{explainProgress ? ` -- ${fmtInt(explainProgress.scanned)} of ${fmtInt(explainProgress.total)}` : ''}
              </span>
            )}
            {censusPolling && (
              <span className="text-xs text-muted-foreground tabular-nums flex items-center gap-1">
                <RefreshCw className="h-3 w-3 animate-spin" />
                census {censusPoll.elapsed}s{censusProgress ? ` -- ${fmtInt(censusProgress.folders_scanned)} folders, ${fmtInt(censusProgress.duplicate_groups_found)} duplicate group(s)` : ''}
              </span>
            )}
          </div>

          {explainFailed && (
            <p className="text-xs text-red-600">{explainFailed}</p>
          )}

          {explainResult && (
            <div className="space-y-2" data-testid="explain-result">
              <Badge className="text-[10px] bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300">
                EXPLAINED {fmtInt(explainResult.explained)}{explainResult.truncated ? ' (sample, not the full population)' : ''}
              </Badge>

              {explainResult.aggregate_by_source && Object.keys(explainResult.aggregate_by_source).length > 0 && (
                <div className="overflow-x-auto border rounded-lg">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="border-b bg-muted/30">
                        <th className="px-2 py-1 text-left">Pipeline</th>
                        <th className="px-2 py-1 text-right">Sampled</th>
                        <th className="px-2 py-1 text-right">Correct</th>
                        <th className="px-2 py-1 text-right">Same name, different ID</th>
                        <th className="px-2 py-1 text-right">Name mismatch</th>
                        <th className="px-2 py-1 text-right">Length mismatch</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Object.entries(explainResult.aggregate_by_source).map(([source, c]) => (
                        <tr key={source} className="border-b last:border-0">
                          <td className="px-2 py-1 font-mono">{source}</td>
                          <td className="px-2 py-1 text-right">{fmtInt(c.sampled)}</td>
                          <td className="px-2 py-1 text-right text-green-600">{fmtInt(c.correct)}</td>
                          <td className="px-2 py-1 text-right text-amber-600">{fmtInt(c.same_name_different_id)}</td>
                          <td className="px-2 py-1 text-right">{fmtInt(c.name_mismatch)}</td>
                          <td className="px-2 py-1 text-right">{fmtInt(c.length_mismatch)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {explainResult.explanations && explainResult.explanations.length > 0 && (
                <details>
                  <summary className="text-sm font-medium cursor-pointer">
                    Per-document detail ({explainResult.explanations.length})
                  </summary>
                  <div className="mt-2 space-y-2 max-h-96 overflow-y-auto">
                    {explainResult.explanations.map((ex, i) => (
                      <div key={ex.id || i} className="border rounded-lg p-2 text-xs">
                        <p className="font-mono">
                          {ex.source} &middot; {ex.id}{' '}
                          <span className={
                            ex.status === 'correct' ? 'text-green-600'
                              : ex.status === 'mismatched' ? 'text-amber-600' : 'text-red-600'
                          }>
                            {ex.first_divergence ? ex.first_divergence.kind : ex.status}
                          </span>
                        </p>
                        {ex.reason && <p className="text-muted-foreground mt-1">{ex.reason}</p>}
                        {ex.current_chain && ex.canonical_chain && (
                          <div className="mt-1 grid grid-cols-1 sm:grid-cols-2 gap-2">
                            <div>
                              <p className="text-muted-foreground">Current (with folder IDs)</p>
                              {ex.current_chain.map((c, idx) => (
                                <p key={idx} className={
                                  ex.first_divergence && idx === ex.first_divergence.index ? 'text-amber-600 font-medium' : ''
                                }>
                                  {c.name} <span className="text-muted-foreground">({c.id})</span>
                                </p>
                              ))}
                            </div>
                            <div>
                              <p className="text-muted-foreground">Canonical (with folder IDs)</p>
                              {ex.canonical_chain.map((c, idx) => (
                                <p key={idx} className={
                                  ex.first_divergence && idx === ex.first_divergence.index ? 'text-amber-600 font-medium' : ''
                                }>
                                  {c.segment} <span className="text-muted-foreground">
                                    ({c.exists ? c.id : 'not yet created'}{c.duplicate ? `, ${c.candidate_ids.length} duplicates: ${c.candidate_ids.join(', ')}` : ''})
                                  </span>
                                </p>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </details>
              )}
            </div>
          )}

          {censusFailed && (
            <p className="text-xs text-red-600">{censusFailed}</p>
          )}

          {censusResult && (
            <div className="space-y-2" data-testid="census-result">
              <Badge className="text-[10px] bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300">
                {fmtInt(censusResult.duplicate_group_count)} duplicate-sibling group(s) across {fmtInt(censusResult.folders_scanned)} folders scanned
                {censusResult.truncated ? ' (truncated -- raise max_folders and re-run for the rest)' : ''}
              </Badge>
              {censusResult.duplicate_groups && censusResult.duplicate_groups.length > 0 && (
                <div className="overflow-x-auto border rounded-lg max-h-80 overflow-y-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="border-b bg-muted/30">
                        <th className="px-2 py-1 text-left">Parent path</th>
                        <th className="px-2 py-1 text-left">Name</th>
                        <th className="px-2 py-1 text-left">Folder IDs (file counts)</th>
                        <th className="px-2 py-1 text-left">Currently picked</th>
                      </tr>
                    </thead>
                    <tbody>
                      {censusResult.duplicate_groups.map((g, i) => (
                        <tr key={i} className="border-b last:border-0 align-top">
                          <td className="px-2 py-1 text-muted-foreground">{g.parent_path}</td>
                          <td className="px-2 py-1 font-medium">{g.name}</td>
                          <td className="px-2 py-1 font-mono">
                            {g.folder_ids.map((id) => (
                              <div key={id} className="flex items-center gap-2 py-0.5">
                                <span>{id} ({g.file_counts?.[id] ?? '?'} files)</span>
                                {g.file_counts?.[id] === 0 && id !== g.picked_by_current_resolution && (
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="h-5 px-1.5 text-[10px] font-sans"
                                    disabled={busy || twinBusyId != null}
                                    onClick={() => runDeleteEmptyTwin(g, id)}
                                  >
                                    {twinBusyId === id ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Delete empty twin'}
                                  </Button>
                                )}
                              </div>
                            ))}
                          </td>
                          <td className="px-2 py-1 font-mono">{g.picked_by_current_resolution}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {raceAudit && (
            <div className="border rounded-lg p-2 space-y-1 text-xs" data-testid="race-audit-result">
              <p className="font-medium">{raceAudit.function}</p>
              <p className="text-muted-foreground">{raceAudit.race_window}</p>
              <p className="text-muted-foreground">{raceAudit.secondary_ordering_hazard}</p>
              <p className="italic">{raceAudit.not_fixed}</p>
              {raceAudit.candidate_merge_strategy_for_design && (
                <details>
                  <summary className="cursor-pointer font-medium">Candidate merge strategy (design only, not implemented)</summary>
                  <ul className="mt-1 list-disc list-inside space-y-1">
                    {raceAudit.candidate_merge_strategy_for_design.map((step, i) => (
                      <li key={i} className="text-muted-foreground">{step}</li>
                    ))}
                  </ul>
                </details>
              )}
            </div>
          )}

          {locateTreeFailed && (
            <p className="text-xs text-red-600">{locateTreeFailed}</p>
          )}

          {locateTreeResult && (
            <div className="space-y-2" data-testid="locate-tree-result">
              <Badge className="text-[10px] bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300">
                LOCATE AUDITED TREE (read-only)
              </Badge>
              <p className="text-[10px] text-muted-foreground">Generated: {locateTreeResult.generated_at}</p>

              {/* (a) writer vs checker root resolution */}
              <details open>
                <summary className="text-sm font-medium cursor-pointer">
                  (a) Writer vs checker root resolution
                  {locateTreeResult.root_resolution?.comparison?.diverges ? (
                    <span className="text-red-600 ml-1">-- DIVERGES</span>
                  ) : (
                    <span className="text-green-600 ml-1">-- match</span>
                  )}
                </summary>
                <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs">
                  {['writer', 'checker'].map((side) => {
                    const v = locateTreeResult.root_resolution?.[side];
                    if (!v) return null;
                    return (
                      <div key={side} className="border rounded-lg p-2">
                        <p className="font-medium">{v.label}</p>
                        <p className="font-mono break-all mt-1">root_folder_id: {v.root_folder_id}</p>
                        <p className="font-mono break-all">root_folder_name: {v.root_folder_name ?? '(lookup failed)'}</p>
                        <p className="font-mono break-all">drive_id: {v.drive_id ?? 'null (My Drive)'}</p>
                        <p className="font-mono break-all">drive_name: {v.drive_name ?? '-'}</p>
                        <p className="font-mono break-all">root_folder_actual_drive_id: {v.root_folder_actual_drive_id ?? 'null'}</p>
                        {v.lookup_errors && v.lookup_errors.length > 0 && (
                          <p className="text-red-600 mt-1">{v.lookup_errors.join('; ')}</p>
                        )}
                      </div>
                    );
                  })}
                </div>
                {locateTreeResult.root_resolution?.comparison?.diverges && (
                  <pre className="mt-2 text-[10px] bg-red-50 dark:bg-red-950/20 border border-red-300 dark:border-red-700 rounded-lg p-2 overflow-x-auto">
                    {JSON.stringify(
                      {
                        field_diffs: locateTreeResult.root_resolution.comparison.field_diffs,
                        scope_kwarg_diffs: locateTreeResult.root_resolution.comparison.scope_kwarg_diffs,
                      },
                      null, 2,
                    )}
                  </pre>
                )}

                {locateTreeResult.jurisdiction_candidate_lookup_under_root && (
                  <div className="mt-2 overflow-x-auto border rounded-lg">
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="border-b bg-muted/30">
                          <th className="px-2 py-1 text-left">Jurisdiction folder</th>
                          <th className="px-2 py-1 text-left">Writer's candidates</th>
                          <th className="px-2 py-1 text-left">Checker's candidates</th>
                        </tr>
                      </thead>
                      <tbody>
                        {Object.entries(locateTreeResult.jurisdiction_candidate_lookup_under_root.writer || {}).map(([name, w]) => {
                          const c = locateTreeResult.jurisdiction_candidate_lookup_under_root.checker?.[name];
                          const diverges = !!locateTreeResult.jurisdiction_candidate_lookup_under_root.diffs?.[name];
                          return (
                            <tr key={name} className={`border-b last:border-0 align-top ${diverges ? 'bg-red-50 dark:bg-red-950/20' : ''}`}>
                              <td className="px-2 py-1 font-medium">{name}</td>
                              <td className="px-2 py-1 font-mono">{w?.error ? `error: ${w.error}` : (w?.candidate_ids?.join(', ') || 'none')}</td>
                              <td className="px-2 py-1 font-mono">{c?.error ? `error: ${c.error}` : (c?.candidate_ids?.join(', ') || 'none')}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </details>

              {/* (b) physical location of sampled already-moved docs */}
              <details open>
                <summary className="text-sm font-medium cursor-pointer">
                  (b) Physical location of {fmtInt(locateTreeResult.sample_relocated_docs?.found)} sampled already-moved doc(s)
                </summary>
                {locateTreeResult.sample_relocated_docs?.classification_counts && (
                  <p className="text-xs text-muted-foreground mt-1">
                    {Object.entries(locateTreeResult.sample_relocated_docs.classification_counts)
                      .map(([k, n]) => `${k}: ${n}`).join(' · ')}
                  </p>
                )}
                <div className="mt-2 overflow-x-auto border rounded-lg max-h-64 overflow-y-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="border-b bg-muted/30">
                        <th className="px-2 py-1 text-left">Source</th>
                        <th className="px-2 py-1 text-left">Relocated at</th>
                        <th className="px-2 py-1 text-left">Classification</th>
                        <th className="px-2 py-1 text-left">Real parent chain (leaf&rarr;top)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(locateTreeResult.sample_relocated_docs?.docs || []).map((d, i) => (
                        <tr key={d.id || i} className="border-b last:border-0 align-top">
                          <td className="px-2 py-1 font-mono whitespace-nowrap">{d.source}</td>
                          <td className="px-2 py-1 whitespace-nowrap">{d.drive_relocated_at}</td>
                          <td className={`px-2 py-1 font-medium ${d.classification === 'in_canonical_tree' ? 'text-green-600' : 'text-amber-600'}`}>
                            {d.classification}
                          </td>
                          <td className="px-2 py-1 text-muted-foreground">
                            {d.reason && <div>{d.reason}</div>}
                            {(d.chain || []).map((c, idx) => (
                              <div key={idx}>{c.name} <span className="font-mono">({c.id}{c.drive_id ? `, drive ${c.drive_id}` : ', My Drive'})</span></div>
                            ))}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </details>

              {/* (c) jurisdiction folder global search */}
              <details>
                <summary className="text-sm font-medium cursor-pointer">
                  (c) Jurisdiction folders, searched everywhere the credential can see
                </summary>
                <div className="mt-2 space-y-2">
                  {Object.entries(locateTreeResult.jurisdiction_global_search || {}).map(([name, matches]) => (
                    <div key={name} className="border rounded-lg p-2 text-xs">
                      <p className="font-medium">{name} &mdash; {matches.length} match(es)</p>
                      {matches.map((m, i) => (
                        <div key={m.id || i} className="mt-1 pl-2 border-l">
                          <p className="font-mono">{m.id} ({m.found_via_corpus}{m.drive_id ? `, drive ${m.drive_id}` : ', My Drive'})</p>
                          <p className="text-muted-foreground">
                            {(m.parent_chain || []).map((c) => c.name).reverse().join(' / ') || '(root-level)'}
                          </p>
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              </details>
            </div>
          )}
        </div>

        {/* ── Preview in progress ── */}
        {previewPolling && previewProgress && (
          <div className="space-y-2" data-testid="preview-progress">
            <Badge className="text-[10px] bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300">
              <Loader2 className="h-3 w-3 animate-spin mr-1 inline" />
              SCANNING -- {fmtInt(previewProgress.scanned)} of {fmtInt(previewProgress.total_candidates)} checked
            </Badge>
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
              <div className="border rounded-lg p-2 text-center border-green-200 dark:border-green-800">
                <p className="text-xs text-muted-foreground">Correctly filed</p>
                <p className="text-lg font-semibold tabular-nums text-green-600">{fmtInt(previewProgress.correct_count)}</p>
              </div>
              <div className="border rounded-lg p-2 text-center border-amber-200 dark:border-amber-800">
                <p className="text-xs text-muted-foreground">Misfiled</p>
                <p className="text-lg font-semibold tabular-nums text-amber-600">{fmtInt(previewProgress.mismatched_count)}</p>
              </div>
              <div className="border rounded-lg p-2 text-center">
                <p className="text-xs text-muted-foreground">Outside tree</p>
                <p className="text-lg font-semibold tabular-nums">{fmtInt(previewProgress.outside_tree_count)}</p>
              </div>
              <div className="border rounded-lg p-2 text-center border-red-200 dark:border-red-800">
                <p className="text-xs text-muted-foreground">Doc unresolvable</p>
                <p className="text-lg font-semibold tabular-nums text-red-600">{fmtInt(previewProgress.unresolvable_count)}</p>
              </div>
              <div className="border rounded-lg p-2 text-center border-red-200 dark:border-red-800">
                <p className="text-xs text-muted-foreground">Scan errors</p>
                <p className="text-lg font-semibold tabular-nums text-red-600">{fmtInt(previewProgress.error_count)}</p>
              </div>
            </div>
            {previewProgress.counts_by_source && Object.keys(previewProgress.counts_by_source).length > 0 && (
              <div className="overflow-x-auto border rounded-lg">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b bg-muted/30">
                      <th className="px-2 py-1 text-left">Pipeline</th>
                      <th className="px-2 py-1 text-right">Correct</th>
                      <th className="px-2 py-1 text-right">Misfiled</th>
                      <th className="px-2 py-1 text-right">Outside tree</th>
                      <th className="px-2 py-1 text-right">Unresolvable</th>
                      <th className="px-2 py-1 text-right">Errors</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(previewProgress.counts_by_source).map(([source, c]) => (
                      <tr key={source} className="border-b last:border-0">
                        <td className="px-2 py-1 font-mono">{source}</td>
                        <td className="px-2 py-1 text-right">{fmtInt(c.correct)}</td>
                        <td className="px-2 py-1 text-right text-amber-600">{fmtInt(c.mismatched)}</td>
                        <td className="px-2 py-1 text-right">{fmtInt(c.outside_tree)}</td>
                        <td className="px-2 py-1 text-right text-red-600">{fmtInt(c.unresolvable)}</td>
                        <td className="px-2 py-1 text-right text-red-600">{fmtInt(c.error)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* ── Apply in progress ── */}
        {applyPolling && applyProgress && (
          <div className="space-y-2" data-testid="apply-progress">
            <Badge className="text-[10px] bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300">
              <Loader2 className="h-3 w-3 animate-spin mr-1 inline" />
              RELOCATING -- {fmtInt(applyProgress.scanned)} of {fmtInt(applyProgress.total_candidates)} checked
            </Badge>
            <div className="grid grid-cols-3 gap-2">
              <div className="border rounded-lg p-2 text-center border-green-200 dark:border-green-800">
                <p className="text-xs text-muted-foreground">Moved so far</p>
                <p className="text-lg font-semibold tabular-nums text-green-600">{fmtInt(applyProgress.moved)}</p>
              </div>
              <div className="border rounded-lg p-2 text-center">
                <p className="text-xs text-muted-foreground">Not moved so far</p>
                <p className="text-lg font-semibold tabular-nums">{fmtInt(applyProgress.skipped)}</p>
              </div>
              <div className="border rounded-lg p-2 text-center border-red-200 dark:border-red-800">
                <p className="text-xs text-muted-foreground">Failed so far</p>
                <p className="text-lg font-semibold tabular-nums text-red-600">{fmtInt(applyProgress.failed)}</p>
              </div>
            </div>
            {applyProgress.skipped_by_reason && (
              <p className="text-xs text-muted-foreground">
                Not moved so far, broken down: {fmtInt(applyProgress.skipped_by_reason.already_correct)} already correct
                &middot; {fmtInt(applyProgress.skipped_by_reason.outside_tree)} outside tree
                &middot; {fmtInt(applyProgress.skipped_by_reason.unresolvable)} unresolvable
                &middot; {fmtInt(applyProgress.skipped_by_reason.error)} scan errors (retry)
              </p>
            )}
            {applyProgress.counts_by_source && Object.keys(applyProgress.counts_by_source).length > 0 && (
              <div className="overflow-x-auto border rounded-lg">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b bg-muted/30">
                      <th className="px-2 py-1 text-left">Pipeline</th>
                      <th className="px-2 py-1 text-right">Moved</th>
                      <th className="px-2 py-1 text-right">Not moved</th>
                      <th className="px-2 py-1 text-right">Failed</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(applyProgress.counts_by_source).map(([source, c]) => (
                      <tr key={source} className="border-b last:border-0">
                        <td className="px-2 py-1 font-mono">{source}</td>
                        <td className="px-2 py-1 text-right text-green-600">{fmtInt(c.moved)}</td>
                        <td className="px-2 py-1 text-right">{fmtInt(c.skipped)}</td>
                        <td className="px-2 py-1 text-right text-red-600">{fmtInt(c.failed)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {applyProgress.failed_samples && applyProgress.failed_samples.length > 0 && (
              <details>
                <summary className="text-sm font-medium cursor-pointer text-red-600">
                  Failures so far ({applyProgress.failed_samples.length})
                </summary>
                <div className="mt-2 overflow-x-auto border rounded-lg max-h-48 overflow-y-auto">
                  <table className="w-full text-xs">
                    <tbody>
                      {applyProgress.failed_samples.map((s, i) => (
                        <tr key={s.id || i} className="border-b last:border-0">
                          <td className="px-2 py-1 font-mono">{s.source}</td>
                          <td className="px-2 py-1">{s.error}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </details>
            )}
          </div>
        )}

        {/* ── Preview result ── */}
        {preview && (
          <div className="space-y-3">
            <Badge className="text-[10px] bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300">
              PREVIEW (read-only){preview.truncated ? ` -- scanned ${fmtInt(preview.scanned)} of ${fmtInt(preview.total_candidates)}` : ''}
            </Badge>

            <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
              <div className="border rounded-lg p-2 text-center border-green-200 dark:border-green-800">
                <p className="text-xs text-muted-foreground">Correctly filed</p>
                <p className="text-lg font-semibold tabular-nums text-green-600">{fmtInt(preview.correct_count)}</p>
              </div>
              <div className="border rounded-lg p-2 text-center border-amber-200 dark:border-amber-800">
                <p className="text-xs text-muted-foreground">Misfiled</p>
                <p className="text-lg font-semibold tabular-nums text-amber-600">{fmtInt(preview.mismatched_count)}</p>
              </div>
              <div className="border rounded-lg p-2 text-center">
                <p className="text-xs text-muted-foreground">Outside tree</p>
                <p className="text-lg font-semibold tabular-nums">{fmtInt(preview.outside_tree_count)}</p>
              </div>
              <div className="border rounded-lg p-2 text-center border-red-200 dark:border-red-800">
                <p className="text-xs text-muted-foreground">Doc unresolvable</p>
                <p className="text-lg font-semibold tabular-nums text-red-600">{fmtInt(preview.unresolvable_count)}</p>
              </div>
              <div className="border rounded-lg p-2 text-center border-red-200 dark:border-red-800">
                <p className="text-xs text-muted-foreground">Scan errors (retry)</p>
                <p className="text-lg font-semibold tabular-nums text-red-600">{fmtInt(preview.error_count)}</p>
              </div>
            </div>

            {preview.counts_by_source && Object.keys(preview.counts_by_source).length > 0 && (
              <div className="overflow-x-auto border rounded-lg">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b bg-muted/30">
                      <th className="px-2 py-1 text-left">Pipeline</th>
                      <th className="px-2 py-1 text-right">Correct</th>
                      <th className="px-2 py-1 text-right">Misfiled</th>
                      <th className="px-2 py-1 text-right">Outside tree</th>
                      <th className="px-2 py-1 text-right">Unresolvable</th>
                      <th className="px-2 py-1 text-right">Errors</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(preview.counts_by_source).map(([source, c]) => (
                      <tr key={source} className="border-b last:border-0">
                        <td className="px-2 py-1 font-mono">{source}</td>
                        <td className="px-2 py-1 text-right">{fmtInt(c.correct)}</td>
                        <td className="px-2 py-1 text-right text-amber-600">{fmtInt(c.mismatched)}</td>
                        <td className="px-2 py-1 text-right">{fmtInt(c.outside_tree)}</td>
                        <td className="px-2 py-1 text-right text-red-600">{fmtInt(c.unresolvable)}</td>
                        <td className="px-2 py-1 text-right text-red-600">{fmtInt(c.error)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {preview.mismatched_samples && preview.mismatched_samples.length > 0 && (
              <details open>
                <summary className="text-sm font-medium cursor-pointer">
                  Misfiled samples ({preview.mismatched_samples.length})
                </summary>
                <div className="mt-2 overflow-x-auto border rounded-lg max-h-64 overflow-y-auto">
                  <table className="w-full text-xs">
                    <tbody>
                      {preview.mismatched_samples.map((s, i) => (
                        <tr key={s.id || i} className="border-b last:border-0 align-top">
                          <td className="px-2 py-1 font-mono whitespace-nowrap">{s.source}</td>
                          <td className="px-2 py-1">{s.supplier_client_name}</td>
                          <td className="px-2 py-1 text-muted-foreground">
                            <div>current: {s.current_path}</div>
                            <div>canonical: {s.canonical_path}</div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </details>
            )}

            {preview.outside_tree_samples && preview.outside_tree_samples.length > 0 && (
              <details>
                <summary className="text-sm font-medium cursor-pointer">
                  Outside-tree / unreadable samples ({preview.outside_tree_samples.length})
                </summary>
                <div className="mt-2 overflow-x-auto border rounded-lg max-h-48 overflow-y-auto">
                  <table className="w-full text-xs">
                    <tbody>
                      {preview.outside_tree_samples.map((s, i) => (
                        <tr key={s.id || i} className="border-b last:border-0">
                          <td className="px-2 py-1 font-mono whitespace-nowrap">{s.source}</td>
                          <td className="px-2 py-1">{s.reason}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </details>
            )}

            {preview.unresolved_samples && preview.unresolved_samples.length > 0 && (
              <details>
                <summary className="text-sm font-medium cursor-pointer text-red-600">
                  Unresolvable samples ({preview.unresolved_samples.length})
                </summary>
                <div className="mt-2 overflow-x-auto border rounded-lg max-h-48 overflow-y-auto">
                  <table className="w-full text-xs">
                    <tbody>
                      {preview.unresolved_samples.map((s, i) => (
                        <tr key={s.id || i} className="border-b last:border-0">
                          <td className="px-2 py-1 font-mono whitespace-nowrap">{s.source}</td>
                          <td className="px-2 py-1">{s.reason}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </details>
            )}

            {preview.error_samples && preview.error_samples.length > 0 && (
              <details open>
                <summary className="text-sm font-medium cursor-pointer text-red-600">
                  Scan errors -- retry, not evidence of misfiling ({preview.error_samples.length})
                </summary>
                <div className="mt-2 overflow-x-auto border rounded-lg max-h-48 overflow-y-auto">
                  <table className="w-full text-xs">
                    <tbody>
                      {preview.error_samples.map((s, i) => (
                        <tr key={s.id || i} className="border-b last:border-0">
                          <td className="px-2 py-1 font-mono whitespace-nowrap">{s.source}</td>
                          <td className="px-2 py-1">{s.reason}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </details>
            )}

            <p className="text-[10px] text-muted-foreground">Generated: {preview.generated_at}</p>
          </div>
        )}

        {/* ── Preview failed ── */}
        {previewFailed && (
          <div className="space-y-2">
            <Badge className="text-[10px] bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300">
              PREVIEW FAILED
            </Badge>
            <div className="border-2 rounded-lg p-3 border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-950/20 flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-red-600 flex-shrink-0" />
              <p className="text-xs text-red-600">{previewFailed}</p>
            </div>
            {previewProgress && (
              <p className="text-xs text-muted-foreground">
                Last known progress before failure: {fmtInt(previewProgress.scanned)} of {fmtInt(previewProgress.total_candidates)} checked
                &middot; {fmtInt(previewProgress.correct_count)} correct &middot; {fmtInt(previewProgress.mismatched_count)} misfiled
              </p>
            )}
          </div>
        )}

        {/* ── Apply failed (systemic, e.g. Drive not connected) ── */}
        {applyFailed && (
          <div className="space-y-2">
            <Badge className="text-[10px] bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300">
              APPLY FAILED
            </Badge>
            <div className="border-2 rounded-lg p-3 border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-950/20 flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-red-600 flex-shrink-0" />
              <p className="text-xs text-red-600">{applyFailed}</p>
            </div>
            {applyProgress && (
              <p className="text-xs text-muted-foreground">
                Last known progress before failure: {fmtInt(applyProgress.scanned)} of {fmtInt(applyProgress.total_candidates)} checked
                &middot; {fmtInt(applyProgress.moved)} moved &middot; {fmtInt(applyProgress.failed)} failed &middot; {fmtInt(applyProgress.skipped)} not moved
              </p>
            )}
          </div>
        )}

        {/* ── Apply result ── */}
        {applyResult && (
          <div className="space-y-3">
            <Badge className="text-[10px] bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300">
              APPLIED{applyResult.truncated ? ` -- scanned ${fmtInt(applyResult.total_scanned)} of ${fmtInt(applyResult.total_candidates)}, run again for the rest` : ''}
            </Badge>

            <div className="grid grid-cols-3 gap-2">
              <div className="border rounded-lg p-2 text-center border-green-200 dark:border-green-800">
                <p className="text-xs text-muted-foreground">Moved</p>
                <p className="text-lg font-semibold tabular-nums text-green-600">{fmtInt(applyResult.moved)}</p>
              </div>
              <div className="border rounded-lg p-2 text-center">
                <p className="text-xs text-muted-foreground">Not moved</p>
                <p className="text-lg font-semibold tabular-nums">{fmtInt(applyResult.skipped)}</p>
              </div>
              <div className="border rounded-lg p-2 text-center border-red-200 dark:border-red-800">
                <p className="text-xs text-muted-foreground">Failed</p>
                <p className="text-lg font-semibold tabular-nums text-red-600">{fmtInt(applyResult.failed)}</p>
              </div>
            </div>

            {applyResult.skipped_by_reason && (
              <p className="text-xs text-muted-foreground">
                Not moved, broken down: {fmtInt(applyResult.skipped_by_reason.already_correct)} already correct
                &middot; {fmtInt(applyResult.skipped_by_reason.outside_tree)} outside tree
                &middot; {fmtInt(applyResult.skipped_by_reason.unresolvable)} unresolvable
                &middot; {fmtInt(applyResult.skipped_by_reason.error)} scan errors (retry)
              </p>
            )}

            {applyResult.counts_by_source && Object.keys(applyResult.counts_by_source).length > 0 && (
              <div className="overflow-x-auto border rounded-lg">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b bg-muted/30">
                      <th className="px-2 py-1 text-left">Pipeline</th>
                      <th className="px-2 py-1 text-right">Moved</th>
                      <th className="px-2 py-1 text-right">Not moved</th>
                      <th className="px-2 py-1 text-right">Failed</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(applyResult.counts_by_source).map(([source, c]) => (
                      <tr key={source} className="border-b last:border-0">
                        <td className="px-2 py-1 font-mono">{source}</td>
                        <td className="px-2 py-1 text-right text-green-600">{fmtInt(c.moved)}</td>
                        <td className="px-2 py-1 text-right">{fmtInt(c.skipped)}</td>
                        <td className="px-2 py-1 text-right text-red-600">{fmtInt(c.failed)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <p className="text-xs text-muted-foreground">
              {applyResult.elapsed_seconds}s elapsed &middot; {fmtInt(applyResult.total_scanned)} scanned
            </p>

            {applyResult.failed_samples && applyResult.failed_samples.length > 0 && (
              <details>
                <summary className="text-sm font-medium cursor-pointer text-red-600">
                  Failed samples ({applyResult.failed_samples.length})
                </summary>
                <div className="mt-2 overflow-x-auto border rounded-lg max-h-48 overflow-y-auto">
                  <table className="w-full text-xs">
                    <tbody>
                      {applyResult.failed_samples.map((s, i) => (
                        <tr key={s.id || i} className="border-b last:border-0">
                          <td className="px-2 py-1 font-mono">{s.source}</td>
                          <td className="px-2 py-1">{s.error}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </details>
            )}

            <p className="text-[10px] text-muted-foreground">
              Started: {applyResult.started_at} | Finished: {applyResult.finished_at}
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
